import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ChartBarIcon, TargetIcon, GlobeIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, ShieldCheckIcon, BeakerIcon, FileTextIcon, ClipboardCheckIcon, AwardIcon, TrendingUpIcon, UserIcon, BuildingIcon, BarChart4Icon, ArrowRightIcon, SearchIcon, FilterIcon, StarIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export const ProductReviewsPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedCertificationLevel, setCertificationLevel] = useState('All');
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 50
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  // Sample product reviews
  const productReviews = [{
    id: 1,
    name: 'BioBalance Pro Prebiotic',
    category: 'Prebiotic',
    company: 'NutriTech Labs',
    image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    healthClaim: 'Supports gut microbiome diversity and digestive health',
    certificationLevel: 3,
    transparencyScore: 92,
    purityResults: 'Passed all safety and purity tests. No contaminants detected.',
    bioavailabilityResults: 'High survivability through digestive simulation. Reaches target areas effectively.',
    efficacySummary: 'Claims fully supported by evidence',
    recommendation: 'Recommended',
    keyFindings: ['Contains all strains listed on label at specified quantities', 'Demonstrates measurable impact on beneficial gut bacteria in lab models', 'Clinical trial showed 42% improvement in microbiome diversity after 8 weeks', 'Statistically significant reduction in digestive discomfort reported by study participants']
  }, {
    id: 2,
    name: 'ImmunoStrength Plus',
    category: 'Immune Support',
    company: 'VitalCore Supplements',
    image: 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    healthClaim: 'Boosts immune function and reduces inflammation',
    certificationLevel: 2,
    transparencyScore: 78,
    purityResults: 'Passed safety tests. Minor variance in vitamin C content (12% below label claim).',
    bioavailabilityResults: 'Moderate absorption rate in digestive simulation.',
    efficacySummary: 'Claims partially supported by evidence',
    recommendation: 'Conditionally Recommended',
    keyFindings: ['Contains key ingredients but with some variance from label claims', 'Shows moderate anti-inflammatory activity in cell models', 'Increases NK cell activity in lab testing', 'No human clinical trials completed yet to verify immune enhancement claims']
  }, {
    id: 3,
    name: 'GutGuard Digestive Enzymes',
    category: 'Digestive Support',
    company: 'BioCare Health',
    image: 'https://images.unsplash.com/photo-1550572017-edd951b55104?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    healthClaim: 'Improves nutrient absorption and reduces digestive discomfort',
    certificationLevel: 1,
    transparencyScore: 85,
    purityResults: 'Passed all safety tests. Enzyme activity matches label claims.',
    bioavailabilityResults: 'Not tested at Level 1 certification.',
    efficacySummary: 'Safety and content verified, efficacy not validated',
    recommendation: 'Verified for Safety',
    keyFindings: ['Contains all enzymes listed at specified activity levels', 'Free from harmful contaminants and allergens', 'Manufacturing follows cGMP standards', 'No efficacy testing completed to verify digestive improvement claims']
  }, {
    id: 4,
    name: 'MicrobiomePlus Daily',
    category: 'Probiotic',
    company: 'Vitality Sciences',
    image: 'https://images.unsplash.com/photo-1576671414121-aa0c81c869e3?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    healthClaim: 'Restores microbiome balance and improves gut health',
    certificationLevel: 2,
    transparencyScore: 88,
    purityResults: 'Passed safety tests. CFU count 15% lower than label claim.',
    bioavailabilityResults: 'Good survivability in acid conditions. Colonization demonstrated in lab models.',
    efficacySummary: 'Claims partially supported by evidence',
    recommendation: 'Recommended with Notes',
    keyFindings: ['Contains all probiotic strains listed but at lower concentrations than claimed', 'Demonstrates ability to temporarily colonize the gut in lab models', 'Shows positive modulation of gut microbiome composition', 'Further human studies needed to validate long-term health benefits']
  }];
  const categories = ['All', 'Probiotic', 'Prebiotic', 'Immune Support', 'Digestive Support'];
  const certificationLevels = ['All', 1, 2, 3];
  const filteredProducts = productReviews.filter(product => {
    const categoryMatch = selectedCategory === 'All' || product.category === selectedCategory;
    const levelMatch = selectedCertificationLevel === 'All' || product.certificationLevel === selectedCertificationLevel;
    return categoryMatch && levelMatch;
  });
  const getCertificationBadge = level => {
    switch (level) {
      case 1:
        return <div className="flex items-center" data-id="element-1365" data-mp="10kwu8eu0j2u7">
            <ShieldCheckIcon size={16} className="text-blue-600 mr-1" data-id="element-1366" data-mp="10m7xmdjjq8ks" />
            <span className="text-sm font-medium" data-id="element-1367" data-mp="1ek6cdk1s470t">
              Level 1: Evidence Screen
            </span>
          </div>;
      case 2:
        return <div className="flex items-center" data-id="element-1368" data-mp="77x58xmi0nof">
            <BeakerIcon size={16} className="text-blue-600 mr-1" data-id="element-1369" data-mp="751655ywzsye" />
            <span className="text-sm font-medium" data-id="element-1370" data-mp="c70aai2hatwe">
              Level 2: Mechanistic Validation
            </span>
          </div>;
      case 3:
        return <div className="flex items-center" data-id="element-1371" data-mp="kz28e9p2ry3i">
            <AwardIcon size={16} className="text-blue-600 mr-1" data-id="element-1372" data-mp="kztsood829eh" />
            <span className="text-sm font-medium" data-id="element-1373" data-mp="pyewsyvlhd87">
              Level 3: Clinical Validation
            </span>
          </div>;
      default:
        return null;
    }
  };
  const getRecommendationBadge = recommendation => {
    switch (recommendation) {
      case 'Recommended':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1374" data-mp="13w3exyya269k">
            <CheckCircleIcon size={12} className="mr-1" data-id="element-1375" data-mp="31p91joq4v4sb" />
            Recommended
          </span>;
      case 'Conditionally Recommended':
      case 'Recommended with Notes':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-1376" data-mp="2y7bm2d7jadh9">
            <AlertCircleIcon size={12} className="mr-1" data-id="element-1377" data-mp="2of5s4xxq1lzf" />
            {recommendation}
          </span>;
      case 'Verified for Safety':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1378" data-mp="2kbr1ldc5pn7q">
            <ShieldCheckIcon size={12} className="mr-1" data-id="element-1379" data-mp="3c59kg95glzb4" />
            Verified for Safety
          </span>;
      case 'Not Recommended':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-1380" data-mp="381lefuwh7brz">
            <XCircleIcon size={12} className="mr-1" data-id="element-1381" data-mp="1wo9g1zjhjld7" />
            Not Recommended
          </span>;
      default:
        return null;
    }
  };
  return <div className="bg-white w-full" data-id="element-1382" data-mp="1rnlfon9d9s6b">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-50" data-id="element-1383" data-mp="3rqwb99ay1zl8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1384" data-mp="3rtscp7m036ef">
          <div className="text-center" data-id="element-1385" data-mp="3vtspkntgb9jd">
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl" data-id="element-1386" data-mp="3vqohhfcv7tnf">
              Ukweli Product Reviews
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-600" data-id="element-1387" data-mp="3mte2pk0u9mdr">
              Evidence-based scientific evaluations that replace subjective
              opinions with verified data
            </p>
          </div>
        </div>
      </section>

      {/* What Product Reviews Are */}
      <section className="py-16 bg-white" data-id="element-1388" data-mp="4nhv5aiy5710">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1389" data-mp="4mg9kc4b1251">
          <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={sectionVariants} transition={{
          duration: 0.8
        }} className="mb-16" data-id="element-1390" data-mp="9krhghb1h2km">
            <div className="flex items-center justify-center mb-8" data-id="element-1391" data-mp="2mrzhc8l3khcy">
              <div className="bg-blue-100 p-3 rounded-full" data-id="element-1392" data-mp="2mq69ipyqzr3d">
                <div size={28} className="text-blue-600" data-id="element-1393" data-mp="2hsmxlwpivqel" />
              </div>
              <h2 className="ml-4 text-3xl font-bold text-gray-900" data-id="element-1394" data-mp="2vl2qw5smga2b">
                What Product Reviews Are
              </h2>
            </div>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center mb-12" data-id="element-1395" data-mp="2rlc5khlv6m56">
              At the Ukweli Institute, Product Reviews aren't like the casual
              consumer reviews you see on Amazon or blogs — they are
              evidence-based scientific evaluations tied to our certification
              system.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1396" data-mp="27uz0j04g26t9">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="bg-gray-50 p-6 rounded-lg shadow-sm" data-id="element-1397" data-mp="3o9i4cnw3ukbj">
                <div className="flex items-center mb-4" data-id="element-1398" data-mp="kyta75lvz0xe">
                  <FileTextIcon size={20} className="text-blue-600 mr-2" data-id="element-1399" data-mp="kybh2huit2tp" />
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-1400" data-mp="g004w0gr6g8b">
                    Transparent Reports
                  </h3>
                </div>
                <p className="text-gray-700" data-id="element-1401" data-mp="b300rv3grnju">
                  Each review is a transparent report on a supplement,
                  probiotic, or nutraceutical that has been tested through the
                  Ukweli Certified™ process.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="bg-gray-50 p-6 rounded-lg shadow-sm" data-id="element-1402" data-mp="20dhaa4v0z30q">
                <div className="flex items-center mb-4" data-id="element-1403" data-mp="3s546i5x9sm5o">
                  <ClipboardCheckIcon size={20} className="text-blue-600 mr-2" data-id="element-1404" data-mp="2vdsjsnq4zs56" />
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-1405" data-mp="30dy3nhq60acp">
                    Evidence Over Anecdotes
                  </h3>
                </div>
                <p className="text-gray-700" data-id="element-1406" data-mp="39dl523iw9g5r">
                  Instead of star ratings or anecdotes, the reviews summarize
                  lab results, mechanistic evidence, and clinical outcomes.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="bg-gray-50 p-6 rounded-lg shadow-sm" data-id="element-1407" data-mp="oodtv876nav0">
                <div className="flex items-center mb-4" data-id="element-1408" data-mp="21wlingizohz9">
                  <UserIcon size={20} className="text-blue-600 mr-2" data-id="element-1409" data-mp="1x166qu4jkw5h" />
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-1410" data-mp="2bp9eo925ahwx">
                    Accessible Language
                  </h3>
                </div>
                <p className="text-gray-700" data-id="element-1411" data-mp="26wpxl24914nd">
                  They are written in clear, public-facing language so consumers
                  and clinicians can understand the findings without needing a
                  scientific background.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What They Include */}
      <section className="py-16 bg-gray-50" data-id="element-1412" data-mp="3uoqgvc0a6dkg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1413" data-mp="3gwaiwpm8l5qe">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="mb-16" data-id="element-1414" data-mp="3gtxx21dcyb82">
            <div className="flex items-center justify-center mb-8" data-id="element-1415" data-mp="3gmemg3ffksi">
              <div className="bg-blue-100 p-3 rounded-full" data-id="element-1416" data-mp="8fftocyd3jus">
                <ChartBarIcon size={28} className="text-blue-600" data-id="element-1417" data-mp="8i20299intbf" />
              </div>
              <h2 className="ml-4 text-3xl font-bold text-gray-900" data-id="element-1418" data-mp="2o9g4oag0j2ld">
                What They Include
              </h2>
            </div>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center mb-12" data-id="element-1419" data-mp="38gmppr0d7gdk">
              A typical Ukweli Product Review provides comprehensive information
              about the product's scientific validation.
            </p>
            <div className="bg-white p-8 rounded-lg shadow-md" data-id="element-1420" data-mp="2yqte4q08319b">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1421" data-mp="2up83lbb5uz2a">
                <div data-id="element-1422" data-mp="3rez0fh6xcyb9">
                  <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1423" data-mp="3rfh42yvx2et7">
                    Basic Profile
                  </h3>
                  <ul className="space-y-3 text-gray-700" data-id="element-1424" data-mp="3mh5u8o5mg4az">
                    <li className="flex items-start" data-id="element-1425" data-mp="3ie0w0xskqos5">
                      <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1426" data-mp="3icfzj2mp8vrn" />
                      <span data-id="element-1427" data-mp="2705e5haac1x8">Product name and manufacturer</span>
                    </li>
                    <li className="flex items-start" data-id="element-1428" data-mp="21zrh0ua82cdz">
                      <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1429" data-mp="2223twt14hwk6" />
                      <span data-id="element-1430" data-mp="mhl5e27nq6qd">
                        Category (e.g., probiotic, vitamin, herbal blend)
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1431" data-mp="v950med35ygl">
                      <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1432" data-mp="vaoejyb9rxf7" />
                      <span data-id="element-1433" data-mp="156i1mbmm79b8">Intended health claim</span>
                    </li>
                  </ul>
                  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4" data-id="element-1434" data-mp="1a7p5hzlmzocm">
                    Certification Level
                  </h3>
                  <ul className="space-y-3 text-gray-700" data-id="element-1435" data-mp="1e64zwok1rz02">
                    <li className="flex items-start" data-id="element-1436" data-mp="3bzc3il3y4jil">
                      <ShieldCheckIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1437" data-mp="3fzmao13h5fz1" />
                      <span data-id="element-1438" data-mp="n5duag02jo8s">Level 1: Evidence Screen</span>
                    </li>
                    <li className="flex items-start" data-id="element-1439" data-mp="i7ltewhg4rsg">
                      <BeakerIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1440" data-mp="w5k3vqsnc42o" />
                      <span data-id="element-1441" data-mp="s6bfz2kaf0f1">Level 2: Mechanistic Validation</span>
                    </li>
                    <li className="flex items-start" data-id="element-1442" data-mp="1625f4tv9ebey">
                      <AwardIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1443" data-mp="115oq7xg8jt0t" />
                      <span data-id="element-1444" data-mp="1fvm9q037zujo">Level 3: Clinical Validation</span>
                    </li>
                  </ul>
                </div>
                <div data-id="element-1445" data-mp="1oty2o8rk74fa">
                  <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1446" data-mp="1ov996gce2qa3">
                    Testing Results
                  </h3>
                  <ul className="space-y-3 text-gray-700" data-id="element-1447" data-mp="1jtbkh6l7z39r">
                    <li className="flex items-start" data-id="element-1448" data-mp="23i7zg7zifen8">
                      <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1449" data-mp="28g9g6m9gr1vj" />
                      <span data-id="element-1450" data-mp="1ug8i1ekl28jr">Purity and safety findings</span>
                    </li>
                    <li className="flex items-start" data-id="element-1451" data-mp="1ygilajgzqkux">
                      <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1452" data-mp="1kqynls29odsh" />
                      <span data-id="element-1453" data-mp="1pjhygh9kkutl">Bioavailability and biomarker results</span>
                    </li>
                    <li className="flex items-start" data-id="element-1454" data-mp="1aqfxwgo0u32n">
                      <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1455" data-mp="1fsne5ifvw5as" />
                      <span data-id="element-1456" data-mp="16qnxir8s738d">
                        Evidence from lab models and/or clinical trials
                      </span>
                    </li>
                  </ul>
                  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4" data-id="element-1457" data-mp="1m6kuhbn8glqs">
                    Conclusions
                  </h3>
                  <ul className="space-y-3 text-gray-700" data-id="element-1458" data-mp="1ca816evlo9wk">
                    <li className="flex items-start" data-id="element-1459" data-mp="1ccu7kb6r8jd7">
                      <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1460" data-mp="1884cgojx78tc" />
                      <span data-id="element-1461" data-mp="yjmhwp28hxy5">
                        Efficacy Summary: Whether claims are supported,
                        partially supported, or unsupported by the evidence
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1462" data-mp="om81uqk83b6d">
                      <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1463" data-mp="ojc535k42sji" />
                      <span data-id="element-1464" data-mp="fla6ewnuobze">
                        Transparency Score: How openly the company shared data
                        and cooperated with testing
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1465" data-mp="2w91ckk5c15go">
                      <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1466" data-mp="309lb9s5k0h4x" />
                      <span data-id="element-1467" data-mp="28hm6qheck60o">
                        Recommendation: Whether the product is "Ukweli
                        Certified" and what that means for consumers
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product Review Examples */}
      <section className="py-16 bg-white" data-id="element-1468" data-mp="1otd2va3fyblc">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1469" data-mp="1osbpv02v7f0z">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="mb-12" data-id="element-1470" data-mp="1ts6k9dlummqs">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4" data-id="element-1471" data-mp="1a5vh74oj1p7f">
              Featured Product Reviews
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto text-center mb-8" data-id="element-1472" data-mp="hjj1xtwcoee0">
              Explore our evidence-based evaluations of wellness products
            </p>
            {/* Filters */}
            <div className="bg-gray-50 p-4 rounded-lg mb-8" data-id="element-1473" data-mp="rgx890ok7msl">
              <div className="flex flex-col md:flex-row justify-between items-center" data-id="element-1474" data-mp="rfdrl8yzr9qa">
                <div className="flex items-center mb-4 md:mb-0" data-id="element-1475" data-mp="wa03recwd6bf">
                  <FilterIcon size={20} className="text-gray-500 mr-2" data-id="element-1476" data-mp="3uwvvj5bn7wlf" />
                  <span className="text-gray-700 font-medium" data-id="element-1477" data-mp="3r2vej48py8c">Filter by:</span>
                </div>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-1478" data-mp="3se0wx1w4hly3">
                  <div data-id="element-1479" data-mp="3sb50xj3u57ca">
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1480" data-mp="3neo3fyaqxgmj">
                      Category
                    </label>
                    <select id="category" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" data-id="element-1481" data-mp="3df6yxk78d8s5">
                      {categories.map(category => <option key={category} value={category} data-id="element-1482" data-mp="2unmithoaasvm">
                          {category}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-1483" data-mp="2lrewvzt54y4i">
                    <label htmlFor="certificationLevel" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1484" data-mp="2b3fkwjdn5ivh">
                      Certification Level
                    </label>
                    <select id="certificationLevel" value={selectedCertificationLevel} onChange={e => setCertificationLevel(e.target.value === 'All' ? 'All' : parseInt(e.target.value))} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" data-id="element-1485" data-mp="2253kjpmz36pu">
                      {certificationLevels.map(level => <option key={level} value={level} data-id="element-1486" data-mp="3iikac62etc9m">
                          {level === 'All' ? 'All Levels' : `Level ${level}`}
                        </option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          {/* Product Review Cards */}
          <div className="grid grid-cols-1 gap-8" data-id="element-1487" data-mp="hn0h91k67nt">
            {filteredProducts.length > 0 ? filteredProducts.map(product => <motion.div key={product.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-md" data-id="element-1488" data-mp="38y840abf88jj">
                  <div className="flex flex-col md:flex-row" data-id="element-1489" data-mp="2yve6943iqaso">
                    <div className="md:w-1/3 h-64 md:h-auto" data-id="element-1490" data-mp="2ywxov1cncxxm">
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-1491" data-mp="33uh4a4l2zgr5" />
                    </div>
                    <div className="p-6 md:w-2/3" data-id="element-1492" data-mp="3cs9i14b5mgcf">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4" data-id="element-1493" data-mp="3hu8io6fiez5o">
                        <div data-id="element-1494" data-mp="3mria1rmglkw5">
                          <div className="mb-2" data-id="element-1495" data-mp="3mqp6ezisrbi8">
                            <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full uppercase font-semibold tracking-wide" data-id="element-1496" data-mp="3riql0s3ftebf">
                              {product.category}
                            </span>
                          </div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-1497" data-mp="9dn8imb5600z">
                            {product.name}
                          </h3>
                          <p className="text-gray-600 text-sm mb-2" data-id="element-1498" data-mp="1twh97uidkcg6">
                            By {product.company}
                          </p>
                          {getCertificationBadge(product.certificationLevel)}
                        </div>
                        <div className="mt-4 sm:mt-0" data-id="element-1499" data-mp="1fw6h6o5bwrtf">
                          {getRecommendationBadge(product.recommendation)}
                        </div>
                      </div>
                      <div className="mb-4" data-id="element-1500" data-mp="28kkh59fz9y7m">
                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1501" data-mp="28ln81hdaw0nj">
                          Health Claim
                        </h4>
                        <p className="text-gray-700 italic" data-id="element-1502" data-mp="1yvapfup1qhk1">
                          "{product.healthClaim}"
                        </p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4" data-id="element-1503" data-mp="2wc9c6xk6p0s9">
                        <div data-id="element-1504" data-mp="2ri560giqsws8">
                          <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1505" data-mp="3afou93s0g0jt">
                            Key Findings
                          </h4>
                          <ul className="space-y-1 text-sm text-gray-700" data-id="element-1506" data-mp="3fgw1n1q8qxpy">
                            {product.keyFindings.map((finding, index) => <li key={index} className="flex items-start" data-id="element-1507" data-mp="35o6xkrx0sm4n" data-mpi={index}>
                                <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1508" data-mp="2rpgvsyz99y9y" />
                                <span data-id="element-1509" data-mp="2ip0zc9gxjrel">{finding}</span>
                              </li>)}
                          </ul>
                        </div>
                        <div data-id="element-1510" data-mp="gx5xsmoe13a3">
                          <div className="mb-4" data-id="element-1511" data-mp="gww4oq81qg06">
                            <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-1512" data-mp="gvbd99h13d6j">
                              Efficacy Summary
                            </h4>
                            <p className="text-gray-700" data-id="element-1513" data-mp="25epb3l8u4t7v">
                              {product.efficacySummary}
                            </p>
                          </div>
                          <div data-id="element-1514" data-mp="2f7d3m3ss8b0s">
                            <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-1515" data-mp="2f8fx8jhhorke">
                              Transparency Score
                            </h4>
                            <div className="flex items-center" data-id="element-1516" data-mp="2p6vn6j4s5usb">
                              <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2" data-id="element-1517" data-mp="2k4wjt991ixvd">
                                <div className="bg-blue-600 h-2.5 rounded-full" style={{
                            width: `${product.transparencyScore}%`
                          }} data-id="element-1518" data-mp="2y4echzpgiva0"></div>
                              </div>
                              <span className="text-sm font-medium text-gray-700" data-id="element-1519" data-mp="yufw556wqvvk">
                                {product.transparencyScore}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-6" data-id="element-1520" data-mp="mrbo4p7cmxv7">
                        <Link to={`/product/${product.id}`} className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-1521" data-mp="robvr4juk8of">
                          Read full review{' '}
                          <ArrowRightIcon size={16} className="ml-1" data-id="element-1522" data-mp="3yhm0vutr08e" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>) : <div className="text-center py-12 bg-gray-50 rounded-lg" data-id="element-1523" data-mp="1fcldnvdxwcaa">
                <SearchIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-1524" data-mp="1kc810xnhf8wt" />
                <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1525" data-mp="34usayg1uja59">
                  No products found
                </h3>
                <p className="text-gray-600" data-id="element-1526" data-mp="2zvfcsz5iygnd">
                  Try adjusting your filters to find more products
                </p>
              </div>}
          </div>
        </div>
      </section>

      {/* Why Product Reviews Matter */}
      <section className="py-16 bg-gray-50" data-id="element-1527" data-mp="2q3t5vf07z3qf">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1528" data-mp="2m4tupcnwaaqj">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="mb-16" data-id="element-1529" data-mp="2m1xv7jwefdzz">
            <div className="flex items-center justify-center mb-8" data-id="element-1530" data-mp="ae80i820bceg">
              <div className="bg-blue-100 p-3 rounded-full" data-id="element-1531" data-mp="5ferosxo09km">
                <TargetIcon size={28} className="text-blue-600" data-id="element-1532" data-mp="x62lq0juvunh" />
              </div>
              <h2 className="ml-4 text-3xl font-bold text-gray-900" data-id="element-1533" data-mp="x3znwucgox87">
                Why Product Reviews Matter
              </h2>
            </div>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center mb-12" data-id="element-1534" data-mp="16w5cdu3i8win">
              Evidence-based product reviews create value for multiple
              stakeholders in the wellness ecosystem.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1535" data-mp="e7q8o3ptq0mm">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="bg-blue-50 p-8 rounded-lg shadow-md" data-id="element-1536" data-mp="98wwqfq6qhok">
                <div className="flex items-center mb-6" data-id="element-1537" data-mp="224pnb716qtmw">
                  <UserIcon size={24} className="text-blue-600 mr-3" data-id="element-1538" data-mp="227bw19ztbf6p" />
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1539" data-mp="275n3je2mxdlr">
                    For Consumers
                  </h3>
                </div>
                <p className="text-gray-700 mb-4" data-id="element-1540" data-mp="1x5mhvlhamlee">
                  They can finally cut through marketing hype and see which
                  products actually deliver measurable benefits.
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-1541" data-mp="2pv376a9m9m6e">
                  <li className="flex items-start" data-id="element-1542" data-mp="2pwo0xxo3x137">
                    <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1543" data-mp="2c23u0exb4420" />
                    <span data-id="element-1544" data-mp="33ucq3hp7iue2">Make informed decisions based on evidence</span>
                  </li>
                  <li className="flex items-start" data-id="element-1545" data-mp="38l1eup9i5d9i">
                    <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1546" data-mp="38luopz3rcivv" />
                    <span data-id="element-1547" data-mp="3efpvp1tjy06g">
                      Understand what products actually do in the body
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1548" data-mp="39escqn0px23w">
                    <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1549" data-mp="34hs6aazyhxc9" />
                    <span data-id="element-1550" data-mp="2zka7vejkf0ii">Avoid wasting money on ineffective products</span>
                  </li>
                </ul>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="bg-blue-50 p-8 rounded-lg shadow-md" data-id="element-1551" data-mp="2qp2j438oymdk">
                <div className="flex items-center mb-6" data-id="element-1552" data-mp="26my9on9ce3t6">
                  <BuildingIcon size={24} className="text-blue-600 mr-3" data-id="element-1553" data-mp="26pkg2jkhyd9t" />
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1554" data-mp="2bndjx5ybuv6x">
                    For Companies
                  </h3>
                </div>
                <p className="text-gray-700 mb-4" data-id="element-1555" data-mp="1iyezg26qfgyg">
                  A strong review backed by certification builds brand trust and
                  credibility in a competitive $120B+ industry.
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-1556" data-mp="1sw2w4xnweyj9">
                  <li className="flex items-start" data-id="element-1557" data-mp="1su9r1msxomdd">
                    <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1558" data-mp="1xo4ff3jhhpis" />
                    <span data-id="element-1559" data-mp="156s2e04yu4em">
                      Differentiate products with scientific validation
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1560" data-mp="36wpk7bz0htt0">
                    <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1561" data-mp="31x4k2y83vfep" />
                    <span data-id="element-1562" data-mp="3gn22syn9ncwi">Build consumer trust through transparency</span>
                  </li>
                  <li className="flex items-start" data-id="element-1563" data-mp="3btpphko1esj2">
                    <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1564" data-mp="3plw432bjkrge" />
                    <span data-id="element-1565" data-mp="3lmwvn7qlqck7">Gain credibility with healthcare professionals</span>
                  </li>
                </ul>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="bg-blue-50 p-8 rounded-lg shadow-md" data-id="element-1566" data-mp="3ul8s3qe5g4kk">
                <div className="flex items-center mb-6" data-id="element-1567" data-mp="60f0c66uka30">
                  <BarChart4Icon size={24} className="text-blue-600 mr-3" data-id="element-1568" data-mp="5xsty9vp00md" />
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1569" data-mp="awx71blqmihz">
                    For the Market
                  </h3>
                </div>
                <p className="text-gray-700 mb-4" data-id="element-1570" data-mp="1yl6agr5q4bt">
                  Reviews create pressure for higher standards, shifting the
                  industry toward evidence-first wellness.
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-1571" data-mp="3o4goj5t495ds">
                  <li className="flex items-start" data-id="element-1572" data-mp="3o7cpl309ga6g">
                    <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1573" data-mp="1hfvd5dfd4hwb" />
                    <span data-id="element-1574" data-mp="1kwq4upvxrmvo">Elevate standards across the entire industry</span>
                  </li>
                  <li className="flex items-start" data-id="element-1575" data-mp="1fxmz1384tcmn">
                    <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1576" data-mp="1fw23baa2znn7" />
                    <span data-id="element-1577" data-mp="16xgn3ls30kpl">
                      Create transparency in a historically opaque market
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1578" data-mp="23mfvd0so1mhs">
                    <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1579" data-mp="1ynuwb4p5h8ux" />
                    <span data-id="element-1580" data-mp="1tv1t4l9by8ky">Drive innovation based on real efficacy</span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How They're Shared */}
      <section className="py-16 bg-white" data-id="element-1581" data-mp="2hmqne75pp74j">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1582" data-mp="2hkw3mw66fl21">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="mb-16" data-id="element-1583" data-mp="45e41l9ae0zg">
            <div className="flex items-center justify-center mb-8" data-id="element-1584" data-mp="n3qgpgzepszs">
              <div className="bg-blue-100 p-3 rounded-full" data-id="element-1585" data-mp="n1e1hbl19wqf">
                <GlobeIcon size={28} className="text-blue-600" data-id="element-1586" data-mp="s10ebjwy9ayp" />
              </div>
              <h2 className="ml-4 text-3xl font-bold text-gray-900" data-id="element-1587" data-mp="wu2zb3nnvc3a">
                How They're Shared
              </h2>
            </div>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center mb-12" data-id="element-1588" data-mp="10wpvfgrd7j6a">
              Ukweli Product Reviews are distributed through multiple channels
              to maximize their impact.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1589" data-mp="lpnl5qg6bi2q">
              <div className="bg-white p-6 rounded-lg shadow-md text-center" data-id="element-1590" data-mp="lnszg9x2vlxl">
                <div className="inline-block bg-blue-100 p-4 rounded-full mb-4" data-id="element-1591" data-mp="gpretkk07ek0">
                  <GlobeIcon size={32} className="text-blue-600" data-id="element-1592" data-mp="uqlrwghzy3p0" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1593" data-mp="qo7a7zq2d9zb">
                  Online Directory
                </h3>
                <p className="text-gray-700" data-id="element-1594" data-mp="2zxqr4gboybc">
                  Published in an open-access online directory maintained by
                  Ukweli, searchable by product category, health concern, or
                  certification level.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center" data-id="element-1595" data-mp="70hni6wzhzwy">
                <div className="inline-block bg-blue-100 p-4 rounded-full mb-4" data-id="element-1596" data-mp="3fgb56p31xhfl">
                  <UserIcon size={32} className="text-blue-600" data-id="element-1597" data-mp="3fe6sbhn6d836" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1598" data-mp="3bcvavzegft62">
                  Professional Network
                </h3>
                <p className="text-gray-700" data-id="element-1599" data-mp="361hgeqvlmf7d">
                  Shared with clinicians, researchers, and policymakers through
                  professional channels and healthcare networks.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center" data-id="element-1600" data-mp="2wczichepem7f">
                <div className="inline-block bg-blue-100 p-4 rounded-full mb-4" data-id="element-1601" data-mp="3k0zanlrnyko4">
                  <BarChart4Icon size={32} className="text-blue-600" data-id="element-1602" data-mp="3p0ljlj0gler4" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1603" data-mp="3a3crgn3c06yc">
                  Public Education
                </h3>
                <p className="text-gray-700" data-id="element-1604" data-mp="3f1wc8k66vcp4">
                  Simplified into infographics and video explainers for public
                  education, making the science accessible to everyone.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50" data-id="element-1605" data-mp="bl4ybm8hvo3f">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1606" data-mp="gcmxq5uvdgma">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-blue-50 rounded-xl p-8 md:p-12 text-center" data-id="element-1607" data-mp="gdxz5z0ueca8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1608" data-mp="z9em2tp9qlx7">
              Submit a Product for Review
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8" data-id="element-1609" data-mp="qakj8llcgzb7">
              Are you a manufacturer of wellness products? Get your products
              certified and reviewed by our independent scientific team.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1610" data-mp="3b1tfisjfx279">
              <Link to="/certification" className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-md hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center" data-id="element-1611" data-mp="32j9naud9t1y2">
                Apply for Certification{' '}
                <ArrowRightIcon size={16} className="ml-2" data-id="element-1612" data-mp="37imaxhbyud1p" />
              </Link>
              <Link to="/research" className="px-6 py-3 bg-white text-blue-600 font-medium rounded-md shadow-md hover:bg-gray-100 transition-colors duration-300" data-id="element-1613" data-mp="2jtjjjhrj55u0">
                Learn About Our Research
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
};